import type { ShippingApi } from '@/pages/Business/pages/Shippings/models';
import { useAvailableAmendments } from './useAvailableAmendments';
import { useAmendmentStorage } from './useAmendmentStorage';
import { useApplyAmendmentMutation } from '../services';
import { getErrorMessage } from '@/utils';
import { getScannedCode, type ScannedCodeMode } from '@/utils/scannedCode';
import { useState } from 'react';
import {
  useLazyGetShippingBasicInfoByCodeQuery,
  useLazyGetShippingByNumberQuery,
} from '@/pages/Business/pages/Shippings/services/shippings.service';

const SEARCH_MODE_KEY = 'amendments_search_mode';

const readSearchMode = (): ScannedCodeMode => {
  try {
    return localStorage.getItem(SEARCH_MODE_KEY) === 'COMMERCIAL_NUMBER'
      ? 'COMMERCIAL_NUMBER'
      : 'NUMBER';
  } catch {
    return 'NUMBER';
  }
};

export const useApplyAmendmentController = () => {
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { draft, setShipping: persistShipping, updateDraft } = useAmendmentStorage();
  const [shipping, setShippingState] = useState<ShippingApi | null>(
    () => (draft.shipping as ShippingApi) ?? null
  );
  const [searchMode, setSearchModeState] = useState<ScannedCodeMode>(readSearchMode);

  const [searchKey, setSearchKey] = useState(0);

  const setShipping = (newShipping: ShippingApi | null) => {
    setShippingState(newShipping);
    persistShipping(newShipping);
  };

  const setSearchMode = (mode: ScannedCodeMode) => {
    setSearchModeState(mode);
    try {
      localStorage.setItem(SEARCH_MODE_KEY, mode);
    } catch {}
  };

  const [fetchShipping, { isFetching: isShippingLoading }] = useLazyGetShippingByNumberQuery();
  const [findShippingByCode, { isFetching: isCodeLoading }] =
    useLazyGetShippingBasicInfoByCodeQuery();

  const { amendmentOptions, isLoading: isAmendmentsLoading } = useAvailableAmendments(
    shipping ? Number(shipping.number) : null
  );

  const [applyAmendment, { isLoading: isApplying }] = useApplyAmendmentMutation();

  /*
   * Lo escaneado manda sobre el selector: la etiqueta nuestra (SD + número) se
   * busca por número y el QR de Mercado Libre ({"id":"…",…}) por el id del envío,
   * que es el código externo del pedido Flex.
   */
  const resolveSearch = (input: string): { code: string; mode: ScannedCodeMode } | null => {
    const raw = input.trim();
    const scanned = getScannedCode(raw);
    if (!scanned) return null;
    if (scanned.mode === 'NUMBER') return scanned;
    return { code: scanned.code, mode: scanned.code !== raw ? 'COMMERCIAL_NUMBER' : searchMode };
  };

  const handleSearch = async (input: string) => {
    updateDraft({ searchInput: input });
    const search = resolveSearch(input);
    if (!search) return;
    try {
      let number = search.code;
      if (search.mode === 'COMMERCIAL_NUMBER') {
        const found = await findShippingByCode({
          code: search.code,
          mode: 'COMMERCIAL_NUMBER',
        }).unwrap();
        number = String(found.number);
      } else if (!/^\d+$/.test(number)) {
        setErrorMessage(
          'Ese no es un número de pedido. Si es el código del comercio, elegí "Código externo".'
        );
        return;
      }
      const full = await fetchShipping({ number }).unwrap();
      setShipping(full);
    } catch (error) {
      setErrorMessage(
        getErrorMessage(
          error,
          search.mode === 'COMMERCIAL_NUMBER'
            ? 'No se encontró el pedido con ese código externo.'
            : 'No se encontró el pedido con ese número.'
        )
      );
    }
  };

  const handleApply = async (args: { transitionId: string; reason: string; driverId?: string }) => {
    if (!shipping) return;
    try {
      await applyAmendment({ shippingNumber: Number(shipping.number), ...args }).unwrap();
      setSuccessMessage('Enmienda aplicada correctamente.');
      setShipping(null);
      setSearchKey((k) => k + 1);
    } catch (error) {
      setErrorMessage(
        getErrorMessage(error, 'Error al aplicar la enmienda. Verificá los datos e intentá de nuevo.')
      );
    }
  };

  return {
    shipping,
    isShippingLoading: isShippingLoading || isCodeLoading,
    searchKey,
    draftSearchInput: draft.searchInput as string | undefined,
    searchMode,
    setSearchMode,
    handleSearch,
    amendmentOptions,
    isAmendmentsLoading,
    handleApply,
    isApplying,
    successMessage,
    errorMessage,
    setSuccessMessage,
    setErrorMessage,
  };
};
